import {EmptyPolicyNumber, PolicyNumber, ValidPolicyNumber} from 'ApplicationEntry/models/EnteredPolicyNumber';
import {PackageModel, UnassignedPackageModel} from 'ApplicationEntry/models/ProductsRiders/PackageModel';
import {EmptyString, isNonEmptyString, NonEmptyString, StringUnion} from 'Core/models/EmptyString';
import {ToPartialMatchOptions} from 'Shared/utilities/MapDiscriminatedUnion';

type CriticalIllnessProps = {
  productType: StringUnion;
  planType: StringUnion;
  benefitAmount: number;
  premium: StringUnion;
  calculatedPremium: StringUnion;
  cwa: StringUnion;
  insuredSC: StringUnion;
  spouseSC: StringUnion;
  section125: boolean;
  replacement: boolean;
  policyNumber: PolicyNumber;
  situsState: StringUnion;
  cancer: boolean;
  skinCancer: boolean;
  cardiopulmonaryEnhancement: boolean;
  lifestyleEnhancement: boolean;
  secondEvaluationTransportation: boolean;
  specifiedDiseaseEnhancement: boolean;
  specifiedChronicCI: boolean;
  specifiedChronicCIInjury: boolean;
  recurrence: boolean;
  criticalIllnessEnhancement: boolean;
  wellnessUnits: StringUnion;
  fixedWellnessUnits: StringUnion;
  variableWellnessUnits: StringUnion;
  wellnessPreventiveCareUnits: StringUnion;
  ciBenefit: StringUnion;
  ciOptionII: boolean;
  secondEventCI: boolean;
  secondEventCancer: boolean;
  chronic90Day: boolean;
  suppCIHIV: boolean;
  suppCI: boolean;
  continuationCoverage: boolean;
  chronic365Day: boolean;
  returnOfPremium: boolean;
  threeTier: StringUnion;
  jStamp: boolean;
  packageSource: StringUnion;
  packageSourceList: PackageModel;
  skinCancerUnit: StringUnion;
};

export class InvalidGroupCriticalIllness {
  public readonly key = 'InvalidGroupCriticalIllness';

  constructor(
    public readonly productType: StringUnion,
    public readonly planType: StringUnion,
    public readonly benefitAmount: number,
    public readonly premium: StringUnion,
    public readonly calculatedPremium: StringUnion,
    public readonly cwa: StringUnion,
    public readonly insuredSC: StringUnion,
    public readonly spouseSC: StringUnion,
    public readonly section125: boolean,
    public readonly replacement: boolean,
    public readonly policyNumber: PolicyNumber,
    public readonly situsState: StringUnion,
    public readonly cancer: boolean,
    public readonly skinCancer: boolean,
    public readonly cardiopulmonaryEnhancement: boolean,
    public readonly lifestyleEnhancement: boolean,
    public readonly secondEvaluationTransportation: boolean,
    public readonly specifiedDiseaseEnhancement: boolean,
    public readonly specifiedChronicCI: boolean,
    public readonly specifiedChronicCIInjury: boolean,
    public readonly recurrence: boolean,
    public readonly criticalIllnessEnhancement: boolean,
    public readonly wellnessUnits: StringUnion,
    public readonly fixedWellnessUnits: StringUnion,
    public readonly variableWellnessUnits: StringUnion,
    public readonly wellnessPreventiveCareUnits: StringUnion,
    public readonly ciBenefit: StringUnion,
    public readonly ciOptionII: boolean,
    public readonly secondEventCI: boolean,
    public readonly secondEventCancer: boolean,
    public readonly chronic90Day: boolean,
    public readonly suppCIHIV: boolean,
    public readonly suppCI: boolean,
    public readonly continuationCoverage: boolean,
    public readonly chronic365Day: boolean,
    public readonly returnOfPremium: boolean,
    public readonly threeTier: StringUnion,
    public readonly jStamp: boolean,
    public readonly packageSource: StringUnion,
    public readonly packageSourceList: PackageModel,
    public readonly skinCancerUnit: StringUnion
  ) {}
}

export class ValidGroupCriticalIllness {
  public static createApp({
    productType,
    planType,
    benefitAmount,
    premium,
    calculatedPremium,
    cwa,
    insuredSC,
    spouseSC,
    section125,
    replacement,
    policyNumber,
    situsState,
    cancer,
    skinCancer,
    cardiopulmonaryEnhancement,
    lifestyleEnhancement,
    secondEvaluationTransportation,
    specifiedDiseaseEnhancement,
    specifiedChronicCI,
    specifiedChronicCIInjury,
    recurrence,
    criticalIllnessEnhancement,
    wellnessUnits,
    fixedWellnessUnits,
    variableWellnessUnits,
    wellnessPreventiveCareUnits,
    ciBenefit,
    ciOptionII,
    secondEventCI,
    secondEventCancer,
    chronic90Day,
    suppCIHIV,
    suppCI,
    continuationCoverage,
    chronic365Day,
    returnOfPremium,
    threeTier,
    jStamp,
    packageSource,
    packageSourceList,
    skinCancerUnit,
  }: CriticalIllnessProps) {
    return this.create(
      productType,
      planType,
      benefitAmount,
      premium,
      calculatedPremium,
      cwa,
      insuredSC,
      spouseSC,
      section125,
      replacement,
      policyNumber,
      situsState,
      cancer,
      skinCancer,
      cardiopulmonaryEnhancement,
      lifestyleEnhancement,
      secondEvaluationTransportation,
      specifiedDiseaseEnhancement,
      specifiedChronicCI,
      specifiedChronicCIInjury,
      recurrence,
      criticalIllnessEnhancement,
      wellnessUnits,
      fixedWellnessUnits,
      variableWellnessUnits,
      wellnessPreventiveCareUnits,
      ciBenefit,
      ciOptionII,
      secondEventCI,
      secondEventCancer,
      chronic90Day,
      suppCIHIV,
      suppCI,
      continuationCoverage,
      chronic365Day,
      returnOfPremium,
      threeTier,
      jStamp,
      packageSource,
      packageSourceList,
      skinCancerUnit
    );
  }

  public static create(
    productType: StringUnion,
    planType: StringUnion,
    benefitAmount: number,
    premium: StringUnion,
    calculatedPremium: StringUnion,
    cwa: StringUnion,
    insuredSC: StringUnion,
    spouseSC: StringUnion,
    section125: boolean,
    replacement: boolean,
    policyNumber: PolicyNumber,
    situsState: StringUnion,
    cancer: boolean,
    skinCancer: boolean,
    cardiopulmonaryEnhancement: boolean,
    lifestyleEnhancement: boolean,
    secondEvaluationTransportation: boolean,
    specifiedDiseaseEnhancement: boolean,
    specifiedChronicCI: boolean,
    specifiedChronicCIInjury: boolean,
    recurrence: boolean,
    criticalIllnessEnhancement: boolean,
    wellnessUnits: StringUnion,
    fixedWellnessUnits: StringUnion,
    variableWellnessUnits: StringUnion,
    wellnessPreventiveCareUnits: StringUnion,
    ciBenefit: StringUnion,
    ciOptionII: boolean,
    secondEventCI: boolean,
    secondEventCancer: boolean,
    chronic90Day: boolean,
    suppCIHIV: boolean,
    suppCI: boolean,
    continuationCoverage: boolean,
    chronic365Day: boolean,
    returnOfPremium: boolean,
    threeTier: StringUnion,
    jStamp: boolean,
    packageSource: StringUnion,
    packageSourceList: PackageModel,
    skinCancerUnit: StringUnion
  ) {
    return isNonEmptyString(productType) &&
      planType &&
      benefitAmount &&
      isNonEmptyString(premium) &&
      isNonEmptyString(cwa) &&
      insuredSC &&
      section125 &&
      replacement &&
      policyNumber
      ? new ValidGroupCriticalIllness(
          productType,
          planType,
          benefitAmount,
          premium,
          calculatedPremium,
          cwa,
          insuredSC,
          spouseSC,
          section125,
          replacement,
          policyNumber,
          situsState,
          cancer,
          skinCancer,
          cardiopulmonaryEnhancement,
          lifestyleEnhancement,
          secondEvaluationTransportation,
          specifiedDiseaseEnhancement,
          specifiedChronicCI,
          specifiedChronicCIInjury,
          recurrence,
          criticalIllnessEnhancement,
          wellnessUnits,
          fixedWellnessUnits,
          variableWellnessUnits,
          wellnessPreventiveCareUnits,
          ciBenefit,
          ciOptionII,
          secondEventCI,
          secondEventCancer,
          chronic90Day,
          suppCIHIV,
          suppCI,
          continuationCoverage,
          chronic365Day,
          returnOfPremium,
          threeTier,
          jStamp,
          packageSource,
          packageSourceList,
          skinCancerUnit
        )
      : new InvalidGroupCriticalIllness(
          productType,
          planType,
          benefitAmount,
          premium,
          calculatedPremium,
          cwa,
          insuredSC,
          spouseSC,
          section125,
          replacement,
          policyNumber,
          situsState,
          cancer,
          skinCancer,
          cardiopulmonaryEnhancement,
          lifestyleEnhancement,
          secondEvaluationTransportation,
          specifiedDiseaseEnhancement,
          specifiedChronicCI,
          specifiedChronicCIInjury,
          recurrence,
          criticalIllnessEnhancement,
          wellnessUnits,
          fixedWellnessUnits,
          variableWellnessUnits,
          wellnessPreventiveCareUnits,
          ciBenefit,
          ciOptionII,
          secondEventCI,
          secondEventCancer,
          chronic90Day,
          suppCIHIV,
          suppCI,
          continuationCoverage,
          chronic365Day,
          returnOfPremium,
          threeTier,
          jStamp,
          packageSource,
          packageSourceList,
          skinCancerUnit
        );
  }

  public readonly key = 'ValidGroupCriticalIllness';
  constructor(
    public readonly productType: StringUnion,
    public readonly planType: StringUnion,
    public readonly benefitAmount: number,
    public readonly premium: StringUnion,
    public readonly calculatedPremium: StringUnion,
    public readonly cwa: StringUnion,
    public readonly insuredSC: StringUnion,
    public readonly spouseSC: StringUnion,
    public readonly section125: boolean,
    public readonly replacement: boolean,
    public readonly policyNumber: PolicyNumber,
    public readonly situsState: StringUnion,
    public readonly cancer: boolean,
    public readonly skinCancer: boolean,
    public readonly cardiopulmonaryEnhancement: boolean,
    public readonly lifestyleEnhancement: boolean,
    public readonly secondEvaluationTransportation: boolean,
    public readonly specifiedDiseaseEnhancement: boolean,
    public readonly specifiedChronicCI: boolean,
    public readonly specifiedChronicCIInjury: boolean,
    public readonly recurrence: boolean,
    public readonly criticalIllnessEnhancement: boolean,
    public readonly wellnessUnits: StringUnion,
    public readonly fixedWellnessUnits: StringUnion,
    public readonly variableWellnessUnits: StringUnion,
    public readonly wellnessPreventiveCareUnits: StringUnion,
    public readonly ciBenefit: StringUnion,
    public readonly ciOptionII: boolean,
    public readonly secondEventCI: boolean,
    public readonly secondEventCancer: boolean,
    public readonly chronic90Day: boolean,
    public readonly suppCIHIV: boolean,
    public readonly suppCI: boolean,
    public readonly continuationCoverage: boolean,
    public readonly chronic365Day: boolean,
    public readonly returnOfPremium: boolean,
    public readonly threeTier: StringUnion,
    public readonly jStamp: boolean,
    public readonly packageSource: StringUnion,
    public readonly packageSourceList: PackageModel,
    public readonly skinCancerUnit: StringUnion
  ) {}
}

export type GroupCriticalIllness = InvalidGroupCriticalIllness | ValidGroupCriticalIllness;

export type GroupCriticalIllnessOptions<TReturn> = ToPartialMatchOptions<GroupCriticalIllness, 'key', TReturn>;

export const matchGroupCriticalIllness = <TReturn>(options: GroupCriticalIllnessOptions<TReturn>) => (
  app: GroupCriticalIllness
): TReturn => {
  switch (app.key) {
    case 'InvalidGroupCriticalIllness':
      return options.InvalidGroupCriticalIllness ? options.InvalidGroupCriticalIllness(app) : options.default(app);
    case 'ValidGroupCriticalIllness':
      return options.ValidGroupCriticalIllness ? options.ValidGroupCriticalIllness(app) : options.default(app);
  }
};

export const EmptyGroupCriticalIllness = new InvalidGroupCriticalIllness(
  new EmptyString(),
  new EmptyString(),
  0,
  new EmptyString(),
  new EmptyString(),
  new EmptyString(),
  new EmptyString(),
  new EmptyString(),
  false,
  false,
  EmptyPolicyNumber,
  new EmptyString(),
  false,
  false,
  false,
  false,
  false,
  false,
  false,
  false,
  false,
  false,
  new EmptyString(),
  new EmptyString(),
  new EmptyString(),
  new EmptyString(),
  new EmptyString(),
  false,
  false,
  false,
  false,
  false,
  false,
  false,
  false,
  false,
  new EmptyString(),
  false,
  new EmptyString(),
  new UnassignedPackageModel(),
  new EmptyString()
);

export const changeProductType = (productType: string) => (app: GroupCriticalIllness) =>
  ValidGroupCriticalIllness.createApp({...app, productType: NonEmptyString.create(productType)});

export const changePlanType = (planType: string) => (app: GroupCriticalIllness) =>
  ValidGroupCriticalIllness.createApp({...app, planType: NonEmptyString.create(planType)});

export const changeBenefitAmount = (benefitAmount: number) => (app: GroupCriticalIllness) =>
  ValidGroupCriticalIllness.createApp({...app, benefitAmount});

export const changePremium = (premium: string) => (app: GroupCriticalIllness) =>
  ValidGroupCriticalIllness.createApp({...app, premium: NonEmptyString.create(premium)});

export const changeCalculatedPremium = (calculatedPremium: string) => (app: GroupCriticalIllness) =>
  ValidGroupCriticalIllness.createApp({...app, calculatedPremium: NonEmptyString.create(calculatedPremium)});

export const changeCWA = (cwa: string) => (app: GroupCriticalIllness) =>
  ValidGroupCriticalIllness.createApp({...app, cwa: NonEmptyString.create(cwa)});

export const changeInsuredSC = (insuredSC: string) => (app: GroupCriticalIllness) =>
  ValidGroupCriticalIllness.createApp({...app, insuredSC: NonEmptyString.create(insuredSC)});

export const changeSpouseSC = (spouseSC: string) => (app: GroupCriticalIllness) =>
  ValidGroupCriticalIllness.createApp({...app, spouseSC: NonEmptyString.create(spouseSC)});

export const changeSection125 = (section125: boolean) => (app: GroupCriticalIllness) =>
  ValidGroupCriticalIllness.createApp({...app, section125});

export const changeReplacement = (replacement: boolean) => (app: GroupCriticalIllness) =>
  ValidGroupCriticalIllness.createApp({...app, replacement, policyNumber: ValidPolicyNumber.create(new EmptyString())});

export const changePolicyNumber = (policyNumber: StringUnion) => (app: GroupCriticalIllness) =>
  ValidGroupCriticalIllness.createApp({...app, policyNumber: ValidPolicyNumber.create(policyNumber)});

export const changeSitusState = (situsState: string) => (app: GroupCriticalIllness) =>
  ValidGroupCriticalIllness.createApp({...app, situsState: NonEmptyString.create(situsState)});

export const changeCancerOption = (cancer: boolean) => (app: GroupCriticalIllness) =>
  ValidGroupCriticalIllness.createApp({...app, cancer});

export const changeSkinCancerOption = (skinCancer: boolean) => (app: GroupCriticalIllness) =>
  ValidGroupCriticalIllness.createApp({...app, skinCancer});

export const changeCardiopulmonaryEnhancementOption = (cardiopulmonaryEnhancement: boolean) => (
  app: GroupCriticalIllness
) => ValidGroupCriticalIllness.createApp({...app, cardiopulmonaryEnhancement});

export const changeLifestyleEnhancementOption = (lifestyleEnhancement: boolean) => (app: GroupCriticalIllness) =>
  ValidGroupCriticalIllness.createApp({...app, lifestyleEnhancement});

export const changeSecondEvaluationTransportationOption = (secondEvaluationTransportation: boolean) => (
  app: GroupCriticalIllness
) => ValidGroupCriticalIllness.createApp({...app, secondEvaluationTransportation});
export const changeSpecifiedDiseaseEnhancementOption = (specifiedDiseaseEnhancement: boolean) => (
  app: GroupCriticalIllness
) => ValidGroupCriticalIllness.createApp({...app, specifiedDiseaseEnhancement});
export const changeSpecifiedChronicCIOption = (specifiedChronicCI: boolean) => (app: GroupCriticalIllness) =>
  ValidGroupCriticalIllness.createApp({...app, specifiedChronicCI});
export const changeSpecifiedChronicCIInjuryOption = (specifiedChronicCIInjury: boolean) => (
  app: GroupCriticalIllness
) => ValidGroupCriticalIllness.createApp({...app, specifiedChronicCIInjury});

export const changeRecurrenceOption = (recurrence: boolean) => (app: GroupCriticalIllness) =>
  ValidGroupCriticalIllness.createApp({...app, recurrence});

export const changeCriticalIllnessEnhancement = (criticalIllnessEnhancement: boolean) => (app: GroupCriticalIllness) =>
  ValidGroupCriticalIllness.createApp({...app, criticalIllnessEnhancement});

export const changeWellnessUnit = (wellnessUnits: string) => (app: GroupCriticalIllness) =>
  ValidGroupCriticalIllness.createApp({...app, wellnessUnits: NonEmptyString.create(wellnessUnits)});

export const changeFixedWellnessUnit = (fixedWellnessUnits: string) => (app: GroupCriticalIllness) =>
  ValidGroupCriticalIllness.createApp({...app, fixedWellnessUnits: NonEmptyString.create(fixedWellnessUnits)});

export const changeVariableWellnessUnit = (variableWellnessUnits: string) => (app: GroupCriticalIllness) =>
  ValidGroupCriticalIllness.createApp({...app, variableWellnessUnits: NonEmptyString.create(variableWellnessUnits)});

export const changeSkinCancerUnit = (skinCancerUnit: string) => (app: GroupCriticalIllness) =>
  ValidGroupCriticalIllness.createApp({...app, skinCancerUnit: NonEmptyString.create(skinCancerUnit)});

export const changeWellnessPreventiveCareUnit = (wellnessPreventiveCareUnits: string) => (app: GroupCriticalIllness) =>
  ValidGroupCriticalIllness.createApp({
    ...app,
    wellnessPreventiveCareUnits: NonEmptyString.create(wellnessPreventiveCareUnits),
  });

export const changeCIBenefit = (ciBenefit: string) => (app: GroupCriticalIllness) =>
  ValidGroupCriticalIllness.createApp({...app, ciBenefit: NonEmptyString.create(ciBenefit)});

export const changeCIOptionII = (ciOptionII: boolean) => (app: GroupCriticalIllness) =>
  ValidGroupCriticalIllness.createApp({...app, ciOptionII});

export const changeSecondEventCI = (secondEventCI: boolean) => (app: GroupCriticalIllness) =>
  ValidGroupCriticalIllness.createApp({...app, secondEventCI});

export const changeSecondEventCancer = (secondEventCancer: boolean) => (app: GroupCriticalIllness) =>
  ValidGroupCriticalIllness.createApp({...app, secondEventCancer});

export const changeChronic90Day = (chronic90Day: boolean) => (app: GroupCriticalIllness) =>
  ValidGroupCriticalIllness.createApp({...app, chronic90Day});

export const changeSuppCIHIV = (suppCIHIV: boolean) => (app: GroupCriticalIllness) =>
  ValidGroupCriticalIllness.createApp({...app, suppCIHIV});

export const changeSuppCI = (suppCI: boolean) => (app: GroupCriticalIllness) =>
  ValidGroupCriticalIllness.createApp({...app, suppCI});

export const changeContiuationCoverage = (continuationCoverage: boolean) => (app: GroupCriticalIllness) =>
  ValidGroupCriticalIllness.createApp({...app, continuationCoverage});

export const changeChronic365Day = (chronic365Day: boolean) => (app: GroupCriticalIllness) =>
  ValidGroupCriticalIllness.createApp({...app, chronic365Day});

export const changeReturnOfPremium = (returnOfPremium: boolean) => (app: GroupCriticalIllness) =>
  ValidGroupCriticalIllness.createApp({...app, returnOfPremium});

export const changeThreeTier = (threeTier: string) => (app: GroupCriticalIllness) =>
  ValidGroupCriticalIllness.createApp({...app, threeTier: NonEmptyString.create(threeTier)});

export const changeJStamp = (jStamp: boolean) => (app: GroupCriticalIllness) =>
  ValidGroupCriticalIllness.createApp({...app, jStamp});

export const changePackageSource = (packageSource: string) => (app: GroupCriticalIllness) =>
  ValidGroupCriticalIllness.createApp({...app, packageSource: NonEmptyString.create(packageSource)});

export const changePackageSourceList = (packages: PackageModel) => (app: GroupCriticalIllness) =>
  ValidGroupCriticalIllness.createApp({...app, packageSourceList: packages});
